import { useEffect, useState } from "react";
import { Clock, FileDown } from "lucide-react";
import { EmployeeLayout } from "@/components/EmployeeLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { EMPLOYEE_TIMECLOCK } from "@/constants/testIds";

const STATUS_LABELS = {
  sin_marcar: { label: "Sin marcar", color: "text-charcoal/50" },
  en_turno: { label: "En turno", color: "text-emerald-700" },
  completo: { label: "Completo", color: "text-petroleum" },
};

export default function EmployeeTimeclock() {
  const { user } = useAuth();
  const [summary, setSummary] = useState([]);

  useEffect(() => {
    if (user?.employee_id) api.get(`/employees/${user.employee_id}/timeclock`).then(({ data }) => setSummary(data.daily_summary));
  }, [user]);

  const download = async () => {
    const res = await api.get(`/employees/${user.employee_id}/timeclock/export?format=pdf`, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(res.data);
    link.download = "mi_jornada.pdf";
    link.click();
  };

  return (
    <EmployeeLayout>
      <h1 className="font-heading text-2xl font-bold text-charcoal tracking-tight flex items-center gap-2">
        <Clock className="w-6 h-6 text-petroleum" strokeWidth={1.5} /> Mi Jornada
      </h1>
      <p className="text-charcoal/60 text-sm mt-1">Tu historial de entrada y salida, disponible para ti cuando lo necesites.</p>

      <Button data-testid={EMPLOYEE_TIMECLOCK.exportButton} size="sm" variant="outline" onClick={download} className="mt-4">
        <FileDown className="w-4 h-4 mr-1.5" /> Descargar mi historial (PDF)
      </Button>

      <div className="mt-5 space-y-2">
        {summary.slice().reverse().map((d) => {
          const s = STATUS_LABELS[d.status] || STATUS_LABELS.sin_marcar;
          return (
            <div key={d.date} data-testid={EMPLOYEE_TIMECLOCK.dayRow} className="bg-card border border-petroleum/10 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-charcoal">{d.date}</p>
                <span className={`text-xs font-semibold ${s.color}`}>{s.label}{d.worked_hours != null && ` · ${d.worked_hours} h`}</span>
              </div>
              <p className="text-xs text-charcoal/60 mt-1">
                Entrada: {d.entrada_time || "sin marcar"}
                {d.entrada_location_status === "fuera_rango" && <span className="text-amber-600 font-semibold"> (fuera de rango)</span>}
                {d.entrada_location_status === "sin_ubicacion" && <span className="text-charcoal/40"> (sin ubicación)</span>}
                {" · "}Salida: {d.salida_time || "sin marcar"}
                {d.salida_location_status === "fuera_rango" && <span className="text-amber-600 font-semibold"> (fuera de rango)</span>}
                {d.salida_location_status === "sin_ubicacion" && <span className="text-charcoal/40"> (sin ubicación)</span>}
              </p>
            </div>
          );
        })}
        {summary.length === 0 && <p className="text-sm text-charcoal/50">Todavía no tienes marcajes de jornada.</p>}
      </div>
    </EmployeeLayout>
  );
}
